import { useMemo } from "react";
import styled from "styled-components";
import { media } from "../../../helpers/responsive";
import type { ComparisonWidgetData } from "../types";

interface ComparisonTableProps {
  widgetData: ComparisonWidgetData;
}

const ComparisonTable = ({ widgetData }: ComparisonTableProps) => {
  const barConfig = widgetData?.config?.bar;
  const lineConfig = widgetData?.config?.line;
  const series = widgetData?.data?.series || [];

  const midpoint = Math.ceil(series.length / 2);
  const baseSeries = series.slice(0, midpoint);
  const compareSeries = series.slice(midpoint);

  const basePeriodLabel = baseSeries[0]?.category || "Base Period";
  const comparePeriodLabel = compareSeries[0]?.category || "Comparison";

  const barLabel = useMemo(() => {
    if (!barConfig?.name) return "";
    return barConfig.value_formatter
      ? `${barConfig.name} (${barConfig.value_formatter})`
      : barConfig.name;
  }, [barConfig]);

  const lineLabel = useMemo(() => {
    if (!lineConfig?.name) return "";
    return lineConfig.value_formatter
      ? `${lineConfig.name} (${lineConfig.value_formatter})`
      : lineConfig.name;
  }, [lineConfig]);

  const renderTable = (
    label: string,
    valueKey: "bar_value" | "line_value",
    isSecondary?: boolean
  ) => {
    if (!label) return null;
    return (
      <>
        <h3 className={`table-title ${isSecondary ? "secondary" : ""}`}>{label}</h3>
        <div className="table-scroll">
          <table>
            <colgroup>
              <col className="col-category" />
              <col className="col-value" />
              <col className="col-value" />
            </colgroup>
            <thead>
              <tr>
                <th>Category</th>
                <th className="number-header">{comparePeriodLabel}</th>
                <th className="number-header">{basePeriodLabel}</th>
              </tr>
            </thead>
            <tbody>
              {baseSeries.map((item, i) => (
                <tr key={item.category}>
                  <td className="category-cell">{item.category}</td>
                  <td className="number-cell">
                    {formatNumber(compareSeries[i]?.[valueKey] ?? 0)}
                  </td>
                  <td className="number-cell">
                    {formatNumber(item[valueKey] ?? 0)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </>
    );
  };

  return (
    <TableWrapper>
      {renderTable(barLabel, "bar_value")}
      {renderTable(lineLabel, "line_value", true)}
    </TableWrapper>
  );
};

export default ComparisonTable;

const TableWrapper = styled.div`
  .table-title {
    font-size: 18px;
    font-weight: 600;
    margin: 0 0 16px 0;
    color: ${({ theme }) => theme.text};

    &.secondary {
      margin-top: 32px;
      padding-top: 24px;
      border-top: 1px solid ${({ theme }) => theme.border_color || "#eee"};
    }
  }

  .table-scroll {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  table {
    width: 100%;
    table-layout: fixed;
    border-collapse: separate;
    border-spacing: 0;
    font-variant-numeric: tabular-nums;

    .col-category { width: 30%; }
    .col-value { width: 35%; }
  }

  thead {
    tr {
      background: ${({ theme }) => theme.body};
    }
    th {
      font-size: 13px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.03em;
      color: #888;
      padding: 12px 16px;
      white-space: nowrap;
      border-bottom: 2px solid ${({ theme }) => theme.border_color || "#e8e8e8"};

      &.number-header {
        text-align: right;
      }
    }
  }

  tbody {
    tr {
      transition: background-color 0.15s;
      &:hover {
        background-color: rgba(45, 182, 245, 0.04);
      }
    }

    td {
      padding: 14px 16px;
      font-size: 14px;
      border-bottom: 1px solid ${({ theme }) => theme.border_color || "#f0f0f0"};
      color: ${({ theme }) => theme.text};
    }

    .category-cell {
      font-weight: 500;
    }

    .number-cell {
      text-align: right;
    }
  }

  ${media.down.xs} {
    .table-title {
      font-size: 16px;
    }
    thead th {
      font-size: 11px;
      padding: 10px 12px;
    }
    tbody td {
      font-size: 13px;
      padding: 10px 12px;
    }
  }
`;
